import React, { useState } from 'react';
import Icon from '../../../../components/forms/icon.component';
import ChangePasswordForm from './change-password.form';
import { useForm } from 'react-hook-form';
import { UsersService } from '../../../../services/users.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { STORAGE } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';

interface SettingsModalProps {
  show: boolean;
  onClose: () => void;
  userSession: any;
  onSessionUpdate?: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  show,
  onClose,
  userSession,
  onSessionUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'perfil' | 'seguridad'>('perfil');
  const [mobileView, setMobileView] = useState<'menu' | 'content'>('menu');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset
  } = useForm({
    defaultValues: {
      name: userSession?.user?.name || '',
      email: userSession?.user?.email || '',
      username: userSession?.user?.username || ''
    }
  });

  if (!show) return null;

  const resetForm = () => {
    reset({
      name: userSession?.user?.name || '',
      email: userSession?.user?.email || '',
      username: userSession?.user?.username || ''
    });
  };

  const handleTabChange = (tab: 'perfil' | 'seguridad') => {
    resetForm();
    setShowPasswordForm(false);
    setActiveTab(tab);
    setMobileView('content');
  };

  const onSubmit = async (data: { name: string; email: string; username: string }) => {
    try {
      const updatedUser = await AlertService.toastLoading(
        UsersService.editUser(data),
        'Actualizando información...',
        'Información actualizada',
        'Error al actualizar. Intenta de nuevo más tarde.'
      );
      const currentSession = LocalStorageService.get(STORAGE.SESSION);
      if (currentSession) {
        LocalStorageService.save(STORAGE.SESSION, {
          ...currentSession,
          user: { ...currentSession.user, ...updatedUser }
        });
      }
      reset(data);
      onSessionUpdate?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <style>{`
        @keyframes iosFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes iosSlideIn {
          from { transform: translateY(15px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fade { animation: iosFadeIn 0.25s ease-out; }
        .animate-slide { animation: iosSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .hover-menu-item:hover { background-color: var(--bs-gray-200); opacity: 0.9; }
        [data-bs-theme="dark"] .hover-menu-item:hover { background-color: var(--bs-light); }
      `}</style>

      <div
        className="modal fade show d-block animate-fade"
        tabIndex={-1}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', backdropFilter: 'blur(12px)' }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable px-3 px-md-0">
          <div
            className="modal-content bg-body text-body border-0 rounded-4 shadow-lg overflow-hidden"
            style={{ minHeight: '580px' }}
          >
            {/* Header del Modal */}
            <div className="modal-header border-0 bg-light bg-opacity-50 px-4 py-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                {mobileView === 'content' && (
                  <button
                    className="btn btn-sm btn-icon btn-active-light-primary d-md-none me-2"
                    onClick={() => setMobileView('menu')}
                  >
                    <Icon icon="ki-left" size="fs-2" />
                  </button>
                )}
                <h5 className="modal-title fw-bold fs-5 m-0">
                  {mobileView === 'content' ? 'Detalles' : 'Configuración'}
                </h5>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setMobileView('menu');
                  resetForm();
                  setShowPasswordForm(false);
                  onClose();
                }}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body p-0 d-flex flex-column flex-md-row">
              <div
                className={`col-12 col-md-4 bg-light bg-opacity-40 border-end border-secondary border-opacity-10 p-4 ${mobileView === 'content' ? 'd-none d-md-block' : 'd-block'}`}
              >
                <div className="d-flex align-items-center gap-3 bg-light rounded-3 p-3 mb-4 border border-secondary border-opacity-10">
                  <img
                    src="/assets/media/avatars/av1.png"
                    alt="Avatar"
                    className="rounded-circle border border-2 border-primary border-opacity-20"
                    style={{ width: '52px', height: '52px', objectFit: 'cover' }}
                  />
                  <div className="text-truncate">
                    <h6 className="mb-0 fw-bolder text-truncate">
                      {userSession?.user?.name || 'Usuario'}
                    </h6>
                    <small className="text-muted text-truncate d-block">
                      {userSession?.user?.email || 'Cuenta Activa'}
                    </small>
                  </div>
                </div>

                <div className="nav flex-column gap-2" role="tablist">
                  <button
                    className={`nav-link text-start text-body border-0 rounded-3 px-3 py-2-5 d-flex align-items-center justify-content-between hover-menu-item transition-all ${activeTab === 'perfil' ? 'bg-primary text-white fw-bold shadow-sm' : 'bg-transparent'}`}
                    onClick={() => handleTabChange('perfil')}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="bg-info rounded-3 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <Icon icon="ki-user" size="fs-4" className="text-white" />
                      </div>
                      <span className={activeTab === 'perfil' ? 'text-white' : ''}>Perfil</span>
                    </div>
                    <Icon
                      icon="ki-right"
                      size="fs-6"
                      className={activeTab === 'perfil' ? 'text-white opacity-75' : 'text-muted'}
                    />
                  </button>

                  <button
                    className={`nav-link text-start text-body border-0 rounded-3 px-3 py-2-5 d-flex align-items-center justify-content-between hover-menu-item transition-all ${activeTab === 'seguridad' ? 'bg-primary text-white fw-bold shadow-sm' : 'bg-transparent'}`}
                    onClick={() => handleTabChange('seguridad')}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="bg-success rounded-3 p-1 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <Icon icon="ki-shield-tick" size="fs-4" className="text-white" />
                      </div>
                      <span className={activeTab === 'seguridad' ? 'text-white' : ''}>
                        Seguridad
                      </span>
                    </div>
                    <Icon
                      icon="ki-right"
                      size="fs-6"
                      className={activeTab === 'seguridad' ? 'text-white opacity-75' : 'text-muted'}
                    />
                  </button>
                </div>
              </div>

              <div
                className={`col-12 col-md-8 p-4 p-md-5 overflow-auto bg-body ${mobileView === 'menu' ? 'd-none d-md-block' : 'd-block'}`}
              >
                {activeTab === 'perfil' && (
                  <form onSubmit={handleSubmit(onSubmit)} className="animate-slide">
                    <div className="text-center mb-5">
                      <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex p-3 mb-2">
                        <Icon icon="ki-user" size="fs-2hx" className="text-info" />
                      </div>
                      <h4 className="fw-bolder mb-1">Información del Perfil</h4>
                      <p className="text-muted small">
                        Administra los detalles públicos y de contacto de tu cuenta corporativa.
                      </p>
                    </div>

                    {/* Bloque estilo iOS List Adaptable */}
                    <div className="bg-light bg-opacity-60 rounded-4 p-3 border border-secondary border-opacity-10">
                      <div className="d-flex justify-content-between align-items-center py-3 border-bottom border-secondary border-opacity-10">
                        <span className="text-muted fw-medium">Nombre completo</span>
                        <input
                          className="fw-bold bg-transparent border-0 text-end p-0 text-body"
                          style={{ outline: 'none', width: '200px' }}
                          {...register('name', { required: true })}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-3 border-bottom border-secondary border-opacity-10">
                        <span className="text-muted fw-medium">Nombre de usuario</span>
                        <input
                          className="fw-bold bg-transparent border-0 text-end p-0 text-body"
                          style={{ outline: 'none', width: '200px' }}
                          {...register('username', { required: true })}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center py-3">
                        <span className="text-muted fw-medium">Correo Electrónico</span>
                        <input
                          className="fw-bold text-truncate bg-transparent border-0 text-end p-0 text-body"
                          type="email"
                          style={{ outline: 'none', width: '220px' }}
                          {...register('email', { required: true })}
                        />
                      </div>
                    </div>
                    {isDirty && (
                      <div className="d-flex justify-content-end mt-4">
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm px-5 fw-bold"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Guardando...' : 'Guardar'}
                        </button>
                      </div>
                    )}
                  </form>
                )}

                {activeTab === 'seguridad' && (
                  <div className="animate-slide">
                    <div className="text-center mb-5">
                      <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-3 mb-2">
                        <Icon icon="ki-shield-tick" size="fs-2hx" className="text-success" />
                      </div>
                      <h4 className="fw-bolder mb-1">Seguridad y Accesos</h4>
                      <p className="text-muted small">
                        Configuraciones de protección de identidad y contraseñas de entorno.
                      </p>
                    </div>

                    {/* Bloque estilo iOS List Adaptable */}
                    <div className="bg-light bg-opacity-60 rounded-4 p-3 border border-secondary border-opacity-10">
                      <div className="d-flex justify-content-between align-items-center py-3 border-bottom border-secondary border-opacity-10">
                        <span className="text-muted fw-medium">Rol asignado</span>
                        <span className="badge badge-light-primary fw-bolder text-uppercase px-3 py-1.5 fs-7">
                          {userSession?.user?.roles[0] || 'Operador'}
                        </span>
                      </div>
                      <div className="py-3">
                        {!showPasswordForm ? (
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <span className="d-block fw-bold">Cambiar Contraseña</span>
                              <small className="text-muted d-block fs-7">
                                Es recomendable renovarla cada 90 días.
                              </small>
                            </div>
                            <button
                              className="btn btn-sm btn-light btn-active-light-primary border rounded-pill px-4 fw-bold"
                              onClick={() => setShowPasswordForm(true)}
                            >
                              Modificar
                            </button>
                          </div>
                        ) : (
                          <ChangePasswordForm onCancel={() => setShowPasswordForm(false)} />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsModal;
